/**
 * Context Path System for Deep Nesting Support
 *
 * Replaces the limited (tableId, subtableId, nestedTableId) system with
 * path arrays that can track unlimited nesting depth.
 *
 * Example path for: Main Table → Nested → Subtable → Nested → Subtable
 * {
 *   tableId: "main-id",
 *   path: [
 *     { type: 'nested', id: 'nested-1' },
 *     { type: 'subtable', id: 'sub-1' },
 *     { type: 'nested', id: 'nested-2' },
 *     { type: 'subtable', id: 'sub-2' }
 *   ]
 * }
 */

/**
 * Create a context path from legacy parameters (backward compatibility)
 */
export function createContextPath(tableId, subtableId = null, nestedTableId = null) {
  const path = [];

  // Build path from legacy parameters
  // Order matters: subtable comes before nested when both present
  if (subtableId && nestedTableId) {
    // This is ambiguous in legacy system - assume nested contains subtable
    path.push({ type: 'nested', id: nestedTableId });
    path.push({ type: 'subtable', id: subtableId });
  } else if (subtableId) {
    path.push({ type: 'subtable', id: subtableId });
  } else if (nestedTableId) {
    path.push({ type: 'nested', id: nestedTableId });
  }

  return { tableId, path };
}

/**
 * Extend a context path by adding a new segment
 */
export function extendPath(contextPath, segment) {
  return {
    tableId: contextPath.tableId,
    path: [...contextPath.path, segment]
  };
}

/**
 * Extract legacy parameters for API calls (backward compatibility)
 *
 * IMPORTANT: The backend API only accepts ONE parent ID at a time:
 * - Either parent_nested_table_id (to add something IN a nested table)
 * - OR subtable_id (to add something IN a subtable)
 *
 * So we return the ID of the IMMEDIATE parent container (last element in path).
 */
export function extractLegacyParams(contextPath) {
  const { tableId, path } = contextPath;

  // If no path, we're at the main table level
  if (!path || path.length === 0) {
    return { tableId, subtableId: null, nestedTableId: null };
  }

  // Get the last element - this is the immediate parent container
  const lastSegment = path[path.length - 1];

  if (lastSegment.type === 'subtable') {
    // Adding to a subtable
    return { tableId, subtableId: lastSegment.id, nestedTableId: null };
  } else if (lastSegment.type === 'nested') {
    // Adding to a nested table
    return { tableId, subtableId: null, nestedTableId: lastSegment.id };
  }

  // Fallback (shouldn't happen)
  return { tableId, subtableId: null, nestedTableId: null };
}

/**
 * Get the current container ID (the innermost container)
 */
export function getCurrentContainerId(contextPath) {
  if (!contextPath.path || contextPath.path.length === 0) {
    return contextPath.tableId;
  }
  return contextPath.path[contextPath.path.length - 1].id;
}

/**
 * Get the current container type
 */
export function getCurrentContainerType(contextPath) {
  if (!contextPath.path || contextPath.path.length === 0) {
    return 'table';
  }
  return contextPath.path[contextPath.path.length - 1].type;
}

/**
 * Get nesting depth (0 = main table)
 */
export function getDepth(contextPath) {
  return contextPath.path ? contextPath.path.length : 0;
}

/**
 * Compare two context paths for equality
 */
export function pathsEqual(path1, path2) {
  if (!path1 || !path2) return false;
  if (path1.tableId !== path2.tableId) return false;
  if (path1.path.length !== path2.path.length) return false;

  for (let i = 0; i < path1.path.length; i++) {
    if (path1.path[i].type !== path2.path[i].type ||
        path1.path[i].id !== path2.path[i].id) {
      return false;
    }
  }

  return true;
}

/**
 * Create a cell context from a context path and coordinates
 */
export function createCellContext(contextPath, rowIdx, colIdx) {
  const { tableId, subtableId, nestedTableId } = extractLegacyParams(contextPath);

  return {
    tableId,
    rowIdx,
    colIdx,
    subtableId,
    nestedTableId,
    contextPath,  // Include full path for future use
    isSubtable: !!subtableId
  };
}

/**
 * Check if a cell context matches a context path
 */
export function cellMatchesPath(cellContext, contextPath) {
  if (cellContext.tableId !== contextPath.tableId) return false;

  const { subtableId, nestedTableId } = extractLegacyParams(contextPath);

  return cellContext.subtableId === subtableId &&
         cellContext.nestedTableId === nestedTableId;
}

/**
 * Get parent path (remove last segment)
 */
export function getParentPath(contextPath) {
  if (!contextPath.path || contextPath.path.length === 0) {
    return null;
  }

  return {
    tableId: contextPath.tableId,
    path: contextPath.path.slice(0, -1)
  };
}

/**
 * Convert path to human-readable string (debugging)
 */
export function pathToString(contextPath) {
  let str = `Table[${contextPath.tableId}]`;

  if (contextPath.path && contextPath.path.length > 0) {
    for (const segment of contextPath.path) {
      str += ` → ${segment.type}[${segment.id}]`;
    }
  }

  return str;
}

/**
 * Check if path1 is a parent of path2
 */
export function isParentPath(path1, path2) {
  if (path1.tableId !== path2.tableId) return false;
  if (path1.path.length >= path2.path.length) return false;

  for (let i = 0; i < path1.path.length; i++) {
    if (path1.path[i].type !== path2.path[i].type ||
        path1.path[i].id !== path2.path[i].id) {
      return false;
    }
  }

  return true;
}

/**
 * Build API URL path from context path
 *
 * Example:
 *   Main Table: /api/tables/{tableId}
 *   Main → Nested: /api/tables/{tableId}/nested-table/{nestedId}
 *   Main → Nested → Subtable: /api/tables/{tableId}/nested-table/{nestedId}/subtable/{subtableId}
 *   Main → Subtable: /api/tables/{tableId}/subtable/{subtableId}
 *   Main → Subtable → Nested: /api/tables/{tableId}/subtable/{subtableId}/nested-table/{nestedId}
 */
export function buildApiUrl(contextPath, endpoint = '') {
  const { tableId, path } = contextPath;

  // Start with base table URL
  let url = `/api/tables/${tableId}`;

  // Add each segment in order
  if (path && path.length > 0) {
    for (const segment of path) {
      if (segment.type === 'subtable') {
        url += `/subtable/${segment.id}`;
      } else if (segment.type === 'nested') {
        url += `/nested-table/${segment.id}`;
      }
    }
  }

  // Add the endpoint suffix
  if (endpoint) {
    url += endpoint;
  }

  return url;
}

export default {
  createContextPath,
  extendPath,
  extractLegacyParams,
  getCurrentContainerId,
  getCurrentContainerType,
  getDepth,
  pathsEqual,
  createCellContext,
  cellMatchesPath,
  getParentPath,
  pathToString,
  isParentPath,
  buildApiUrl
};
